import React, { useCallback, useContext, useMemo, useState } from 'react';
import FilterIcon from 'mdi-react/FilterVariantIcon';
import { AppContext } from '@prisjakt/ui-app-context';

import { FilterOverlay } from 'src/components/filter-overlay';
import { SectionItems } from 'src/components/filter-overlay/components/section-items';
import FilterSection from '../section';

import {
  Filter,
  InitialFilters,
  SearchFacetType,
} from 'src/areas/search/components/results/types';
import { AppliedFilter } from '../applied-filters/types';

import * as S from './styles';

type Props = {
  appliedFilters: AppliedFilter[];
  clearAllFilters: () => void;
  clearSingleFilter: (id: string, type: SearchFacetType) => void;
  filters: Filter[];
  onFilterOptionToggle: (
    id: string,
    type: SearchFacetType,
    itemId: string | null,
    values: (string | number)[]
  ) => void;
  productsCount: number;
  selectedFilters: InitialFilters | {};
};

export const FiltersMobile = (props: Props) => {
  const {
    appliedFilters,
    clearAllFilters,
    clearSingleFilter,
    filters,
    onFilterOptionToggle,
    productsCount,
    selectedFilters,
  } = props;

  const { t } = useContext(AppContext);

  const [filterOpen, setFilterOpen] = useState(false);
  const [currentSectionId, setCurrentSectionId] = useState<string>();

  const mobileFilterIcon = useMemo(() => <FilterIcon size={18} />, []);

  const currentSection = useMemo(
    () => filters.find(({ id }) => id === currentSectionId),
    [currentSectionId, filters]
  );

  const toggleMobileFilter = useCallback(() => {
    setFilterOpen(state => !state);
  }, []);

  const appliedFiltersCount = appliedFilters
    .map(item => item.values.length)
    .reduce((a, b) => a + b, 0);

  const clearFilter = useCallback(() => {
    // If currentSection is undefined (index view is open), all filters will be cleared.
    // Otherwise, just the filter for that property will be cleared.
    if (currentSection) {
      clearSingleFilter(currentSection.id, currentSection.type);
    } else {
      clearAllFilters();
    }
  }, [clearAllFilters, clearSingleFilter, currentSection]);

  const getCallToActionText = (option: { id?: string }) => {
    const filter = appliedFilters.find(item => item.filterId === option.id);

    if (!filter) return undefined;
    return filter.values.length > 3
      ? `${filter.values.slice(0, 2).join(', ')}, +${filter.values.length - 2}`
      : filter.values.join(', ');
  };

  const goBack = useCallback(() => {
    setCurrentSectionId(undefined);
  }, []);

  const onClose = useCallback(() => {
    setFilterOpen(false);
    goBack();
  }, [goBack]);

  const hasFiltered = Boolean(
    Object.values(selectedFilters).filter(item => item.selected?.length).length
  );

  const canClear = currentSection
    ? selectedFilters[currentSection.id].selected.length
    : hasFiltered;

  return (
    <div>
      <S.ToggleButton
        count={
          appliedFiltersCount && appliedFiltersCount !== 0
            ? `(${appliedFiltersCount})`
            : undefined
        }
        color={!appliedFiltersCount ? 'quarternary' : 'primary'}
        icon={mobileFilterIcon}
        onToggle={toggleMobileFilter}
        title={t('filter', 'filter.title')}
        variant="outlined"
      />
      {filterOpen && (
        <FilterOverlay
          canClear={canClear}
          clearFilter={clearFilter}
          count={productsCount}
          currentSection={
            currentSection && (
              <FilterSection
                id={currentSection.id}
                onFilterOptionToggle={(itemId, facetType, values) =>
                  onFilterOptionToggle(
                    currentSection.id,
                    currentSection.type,
                    itemId,
                    values
                  )
                }
                options={currentSection.options}
                selectedFilters={selectedFilters}
                title={currentSection.title}
                type={currentSection.type}
                useInfiniteScroll
              />
            )
          }
          hasFiltered={hasFiltered}
          onBack={goBack}
          onClose={onClose}
          sectionItems={
            <SectionItems
              filters={filters}
              getCallToActionText={getCallToActionText}
              setCurrentSectionId={setCurrentSectionId}
            />
          }
          showBackArrow={Boolean(currentSection)}
          showInitialView={!currentSection}
          title={
            currentSection ? currentSection.title : t('filter', 'filter.title')
          }
        />
      )}
    </div>
  );
};
